import { useMemo } from "react";
import { useLocation } from "react-router-dom";
import { LocationPicker } from "@/components/shared/LocationPicker";
import { useLocationNavigation } from "@/hooks/useLocationNavigation";
import { useLocalizedRoutes } from "@/hooks/useLocalizedRoutes";
import { INTERNATIONAL_LOCATION_ID } from "@/constants/config";
import { DOMAIN_DEFAULTS } from "@/lib/domainDefaults";
import { BRAZIL_LOCATION_ID } from "@/constants/branding";
import { cn } from "@/lib/utils";

/**
 * Pages where location acts as a filter (no navigation needed).
 *
 * Route ids resolve through the localized slug map, so the translated paths
 * (/topicos, /especialistas, ...) behave the same as the English ones. The
 * English literals stay as a fallback for the first render, before the slug
 * map has loaded.
 */
const LOCATION_FILTER_ROUTE_IDS = ['experts', 'topics', 'insights', 'explore', 'help'];
const LOCATION_FILTER_FALLBACK_PATHS = [
  '/experts',
  '/topics',
  '/insights',
  '/explore',
  '/help-center',
  '/favorites',
];

interface LocationSelectorProps {
  variant?: 'default' | 'footer';
}

export const LocationSelector = ({ variant = 'default' }: LocationSelectorProps) => {
  const routeLocation = useLocation();
  const { getPath } = useLocalizedRoutes();
  const { 
    selectLocation, 
    selectInternational, 
    currentLocation, 
    isLoading,
  } = useLocationNavigation();

  // Check if current page uses location as a filter
  const filterPaths = useMemo<string[]>(() => {
    const localized = LOCATION_FILTER_ROUTE_IDS
      .map((id) => getPath(id))
      .filter((path): path is string => typeof path === 'string' && path.startsWith('/'));
    return Array.from(new Set([...LOCATION_FILTER_FALLBACK_PATHS, ...localized]));
  }, [getPath]);

  const isFilterPage = filterPaths.some(
    (p) => routeLocation.pathname === p || routeLocation.pathname.startsWith(`${p}/`)
  );

  const handleSelect = (locationId: string) => {
    if (locationId === INTERNATIONAL_LOCATION_ID || locationId === 'GLOBAL') {
      selectInternational({ skipNavigation: isFilterPage });
    } else {
      selectLocation(locationId, { skipNavigation: isFilterPage });
    }
  };


  if (isLoading) {
    return null;
  }

  return (
    <LocationPicker
      value={currentLocation || (DOMAIN_DEFAULTS.isTributario ? BRAZIL_LOCATION_ID : INTERNATIONAL_LOCATION_ID)}
      onSelect={handleSelect}
      variant="ghost"
      size="sm"
      align="end"
      showFlag={false}
      triggerClassName={cn(
        "px-2 sm:px-3 max-w-[180px] sm:max-w-none",
        variant === 'footer' && "text-muted-foreground"
      )}
    />
  );
};
